import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MoreVertical, History, AlertTriangle, ExternalLink, Trash2 } from 'lucide-react';
import { useIsAdmin } from '../hooks/useIsAdmin';
import './ComponentActionsDropdown.css';

interface ComponentActionsDropdownProps {
    buildingKey: string;
    componentKey: string;
    /** Called when "Rapportera fel" is clicked */
    onReportIssue: () => void;
    /** Show "Öppna i ny flik" option (default: true) */
    showOpenInNewTab?: boolean;
    /** Button size variant (default: 'small') */
    size?: 'small' | 'large';
    /** Reset trigger - dropdown closes when this value changes */
    resetTrigger?: unknown;
    /** Called when "Ta bort komponent" is clicked; the item shows only with this, and only to admins */
    onDelete?: () => void;
}

/**
 * Reusable actions dropdown for components.
 * Shows History, Report Issue, and optionally Open in New Tab and (admins) Delete actions.
 */
const ComponentActionsDropdown: React.FC<ComponentActionsDropdownProps> = ({
    buildingKey,
    componentKey,
    onReportIssue,
    showOpenInNewTab = true,
    size = 'small',
    resetTrigger,
    onDelete
}) => {
    const isAdmin = useIsAdmin();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close dropdown when resetTrigger changes (e.g., selected component changes)
    useEffect(() => {
        setIsOpen(false);
    }, [resetTrigger]);

    const close = () => setIsOpen(false);

    const handleReportIssue = () => {
        onReportIssue();
        setIsOpen(false);
    };

    const handleDelete = () => {
        onDelete?.();
        setIsOpen(false);
    };

    const buttonClass = size === 'large'
        ? 'actions-dropdown-button actions-dropdown-button--large'
        : 'actions-dropdown-button';

    const iconSize = size === 'large' ? 20 : 18;

    return (
        <div ref={dropdownRef} className="actions-dropdown">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`${buttonClass} ${isOpen ? 'actions-dropdown-button--active' : ''}`}
                title="Åtgärder"
            >
                <MoreVertical size={iconSize} color="#666" />
            </button>

            {isOpen && (
                <div className="actions-dropdown-menu">
                    {showOpenInNewTab && (
                        <Link
                            to={`/building/${buildingKey}/component/${componentKey}`}
                            target="_blank"
                            onClick={close}
                            className="actions-dropdown-item"
                        >
                            <ExternalLink size={18} color="#6c757d" />
                            <span>Öppna i ny flik</span>
                        </Link>
                    )}
                    <Link
                        to={`/building/${buildingKey}/component/${componentKey}/history`}
                        onClick={close}
                        className="actions-dropdown-item"
                    >
                        <History size={18} color="#6c757d" />
                        <span>Historik</span>
                    </Link>
                    <button
                        type="button"
                        onClick={handleReportIssue}
                        className="actions-dropdown-item"
                    >
                        <AlertTriangle size={18} color="#6c757d" />
                        <span>Rapportera fel</span>
                    </button>
                    {onDelete && isAdmin && (
                        <>
                            <div className="actions-dropdown-divider" role="separator" />
                            <button
                                type="button"
                                onClick={handleDelete}
                                className="actions-dropdown-item actions-dropdown-item--danger"
                            >
                                <Trash2 size={18} />
                                <span>Ta bort komponent</span>
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ComponentActionsDropdown;
